import React, { useRef, useState } from "react";
import {
  Box,
  Button,
  Grid,
  Typography,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
} from "@mui/material";
import { useUser } from "../contexts/useUser";
import { API, Storage } from "aws-amplify";
import { useQueryClient } from "@tanstack/react-query";
import { AgentDocumentsByAgentIdQuery } from "../API";
import { GraphQLResult } from "@aws-amplify/api";

const UrlScrape = ({
  agentId,
  projectId,
  documents,
}: {
  agentId: string;
  projectId: string;
  documents: GraphQLResult<AgentDocumentsByAgentIdQuery> | undefined;
}) => {
  const { user } = useUser();
  const [uploading, setUploading] = useState(false);
  const [url, setUrl] = useState("");
  const [urlError, setUrlError] = useState("");
  const queryClient = useQueryClient();

  const [links, setLinks] = useState([]);

  const scrapeUrl = async (urlToScrape: string) => {
    setUploading(true);

    try {
      const r = await API.post("eventapi", "/scrapeUrlToAgent", {
        body: {
          projectId: projectId,
          agentId: agentId,
          location: urlToScrape,
          uploadDate: new Date().toISOString(),
          uploader: user?.displayName || "",
          type: "url",
        },
      });

      console.log("Scrape response", r);

      queryClient.invalidateQueries({
        queryKey: ["LLMAgentDocuments", { agentId: agentId }],
      });

      //setUrl("");
      //setUrlError("");
    } catch (error) {
      console.error("Error scraping url", error);
    }
    setUploading(false);
  };

  const normalizeUrl = (url: string) => {
    return url.replace(/^https?:\/\/(www\.)?/, "");
  };

  const isUploadedAlready = (url: string) => {
    if (!documents) {
      return false;
    }

    const uploadedDocuments = documents.data?.agentDocumentsByAgentId?.items;

    if (!uploadedDocuments) {
      return false;
    }

    for (let i = 0; i < uploadedDocuments.length; i++) {
      if (
        normalizeUrl(uploadedDocuments[i]?.location ?? "") === normalizeUrl(url)
      ) {
        return true;
      }
    }

    return false;
  };

  return (
    <Box>
      <Typography variant="body2" mb={1}>
        Scrape new or replace URL...
      </Typography>

      <Grid container spacing={2}>
        <Grid item xs={8}>
          <TextField
            label="URL"
            size="small"
            fullWidth
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            error={!!urlError}
            helperText={urlError}
          />
        </Grid>

        <Grid item xs={2}>
          <Button
            size="small"
            variant="contained"
            onClick={async () => {
              if (url === "") {
                console.error("No url entered");
                return;
              }

              // check url is a valid url using regex
              const urlRegex = new RegExp(
                "^(https?://)?([a-z0-9-]+.)+[a-z0-9]{2,4}.*$"
              );
              if (!urlRegex.test(url)) {
                setUrlError("Please enter a valid URL");
                return;
              } else {
                setUrlError("");
              }

              setLinks([]);
              setUploading(true);
              try {
                const r = await API.post("eventapi", "/mapLinksInPage", {
                  body: {
                    url: url,
                    limit: 100
                  },
                });

                if (r && r.success && r.links) {
                  setLinks(r.links);
                  console.log("Links", r.links);
                }
              } finally {
                setUploading(false);
              }
            }}
          >
            Find links
          </Button>
        </Grid>
        <Grid item xs={12}>
          {uploading && (
            <Typography>Scraping... (this can take a few minutes)</Typography>
          )}
        </Grid>
        <Grid item xs={12}>
          {links.length > 0 && (
            <>
              <Typography variant="body2" mb={1}>
                {`Found ${links?.length ?? 0} Links:`}
              </Typography>
              <List>
                {links.map((link, index) => (
                  <ListItem key={index}>
                    <ListItemText primary={link} />
                    <Box display="flex" justifyContent="flex-end">
                      <ListItemButton
                        onClick={() => {
                          window.open(link, "_blank");
                        }}
                      >
                        Preview
                      </ListItemButton>
                      <ListItemButton
                        onClick={async () => {
                          // Add your logic to handle the link addition here
                          console.log("Adding link:", link);
                          await scrapeUrl(link);
                        }}
                      >
                        {isUploadedAlready(link) ? "Refresh" : "Add"}
                      </ListItemButton>
                    </Box>
                  </ListItem>
                ))}
              </List>
            </>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default UrlScrape;
